import home_svg from "./assets/home.svg";
import user_svg from "./assets/user.svg";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faGear,
  faTableTennis,
  faTableTennisPaddleBall,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { CoachHome } from "../pages/common/Home";
import { useTranslation } from "react-i18next";
import {
  isPlayerAccount,
  readActiveHomeRole,
  defaultAuthenticatedPath,
  canSeeDashboardBottomNav,
  canSeeHomeBottomNav,
  isAdminOnlyAccount,
} from "../utils/homeRole";
import { usePlayerLiveTrainingNav } from "../hooks/usePlayerLiveTrainingNav";
import { useCoachTrainingNav } from "../hooks/useCoachTrainingNav";
import {
  persistLiveTrainingSession,
  resolveLiveTrainingRedirect,
} from "../hooks/useLiveTrainingRedirect";
import { requestLiveFullscreen } from "../hooks/useLiveFullscreen";
import {
  OPEN_PLAYER_NEW_SESSION_EVENT,
  requestOpenAdminSidebar,
} from "../tour/tourEvents";
import { clearCoachLiveTrainingAutoEnterSuppress } from "../utils/liveTrainingContext";
import { normalizeStoredGroupInfo } from "../utils/liveTrainingGroupInfo";
import { ensureCoachRoleForLiveTrainingEntry } from "../utils/liveTrainingNav";
import { isMobileDevice } from "../utils/isMobileDevice";
import { primeEvalCameraPermission } from "../utils/evalCameraPermission";

function isHomePath(pathname) {
  return pathname === "/home" || pathname.startsWith("/home/");
}

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export const BottomNavbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const isHome = isHomePath(pathname);
  const [homeMounted, setHomeMounted] = useState(isHome);
  const [user, setUser] = useState(() => readUser());
  const [homeRole, setHomeRole] = useState(() => readActiveHomeRole(readUser()));
  const homeScrollRef = useRef(0);
  const wasHomeRef = useRef(isHome);
  const homeEverLeftRef = useRef(false);

  const isParent = homeRole === "parent";
  const isAdminView = homeRole === "admin" || homeRole === "moderator";
  const isAdminOnly = isAdminOnlyAccount(user);
  const showDashboardNav = canSeeDashboardBottomNav(user);
  const showHomeNav = canSeeHomeBottomNav(user);

  const showCoachNav = useMemo(() => {
    if (isAdminView) return false;
    if (homeRole === "player") return false;
    if (isPlayerAccount(user, { homeRole })) return false;
    return homeRole === "coach" || Number(user?.type_user_id) === 3;
  }, [user, homeRole, isAdminView]);

  const liveNav = usePlayerLiveTrainingNav({ enabled: !showCoachNav && !isAdminView });
  const coachNav = useCoachTrainingNav({ enabled: showCoachNav, pathname });

  useEffect(() => {
    const refresh = () => {
      const u = readUser();
      setUser(u);
      setHomeRole(readActiveHomeRole(u));
    };
    window.addEventListener("smarttt-home-role-changed", refresh);
    window.addEventListener("smarttt-user-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("smarttt-home-role-changed", refresh);
      window.removeEventListener("smarttt-user-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Admin-only accounts have no Home, Planning, or Training.
  // Dual-role admins (coach/player) keep those tabs plus Dashboard.
  useEffect(() => {
    if (isParent) {
      if (isHome || pathname.startsWith("/planning") || pathname.startsWith("/training")) {
        navigate("/parent/progress", { replace: true });
      }
      return;
    }
    if (pathname.startsWith("/parent")) {
      navigate(defaultAuthenticatedPath(user), { replace: true });
      return;
    }
    if (isAdminOnly) {
      if (isHome || pathname.startsWith("/planning") || pathname.startsWith("/training")) {
        navigate(defaultAuthenticatedPath(user), { replace: true });
      }
      return;
    }
    if (showCoachNav) return;
    if (pathname.startsWith("/planning") || pathname.startsWith("/training")) {
      navigate("/home", { replace: true });
    }
  }, [pathname, isHome, isParent, isAdminOnly, showCoachNav, navigate, user]);

  useEffect(() => {
    if (isHome) setHomeMounted(true);
  }, [isHome]);

  const liveTrainingOpen = pathname.startsWith("/live-training");

  useEffect(() => {
    document.documentElement.classList.add("app-bottom-nav-shell");
    return () => document.documentElement.classList.remove("app-bottom-nav-shell");
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("lt-route-active", liveTrainingOpen);
    return () => document.documentElement.classList.remove("lt-route-active");
  }, [liveTrainingOpen]);

  useEffect(() => {
    if (!isHome) return undefined;
    const clampHomeScroll = () => {
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );
      if (window.scrollY > maxScroll) {
        window.scrollTo(0, maxScroll);
      }
    };
    window.addEventListener("scroll", clampHomeScroll, { passive: true });
    return () => window.removeEventListener("scroll", clampHomeScroll);
  }, [isHome]);

  useLayoutEffect(() => {
    if (isHome) {
      if (homeEverLeftRef.current) {
        const y = homeScrollRef.current;
        requestAnimationFrame(() => {
          window.scrollTo(0, y);
        });
      }
    } else if (wasHomeRef.current) {
      homeScrollRef.current = window.scrollY;
      homeEverLeftRef.current = true;
    }
    wasHomeRef.current = isHome;
  }, [isHome]);

  const splitLocation = pathname.split("/");

  const handleDashboardNav = (e) => {
    e.preventDefault();
    const onDashboard = splitLocation[1] === "Dashboard";
    if (onDashboard) {
      requestOpenAdminSidebar();
      return;
    }
    requestOpenAdminSidebar({ deferIfUnmounted: true });
    navigate("/Dashboard");
  };

  const handleLiveTrainingNav = async (e) => {
    e.preventDefault();
    if (liveNav.visible) {
      requestLiveFullscreen();
      const result = await liveNav.join();
      if (result?.error) {
        window.alert(t(result.error) || t("liveTrainingJoinError"));
      }
      return;
    }
    if (location.pathname !== "/home") navigate("/home");
    window.dispatchEvent(new CustomEvent(OPEN_PLAYER_NEW_SESSION_EVENT));
  };

  const handleCoachLiveTrainingNav = async (e) => {
    e.preventDefault();
    if (pathname.startsWith("/live-training")) return;

    clearCoachLiveTrainingAutoEnterSuppress();
    ensureCoachRoleForLiveTrainingEntry(user, user?.permissions || []);

    if (isMobileDevice()) {
      try {
        await primeEvalCameraPermission();
      } catch {
        /* ignore */
      }
      requestLiveFullscreen();
    } else {
      requestLiveFullscreen();
    }

    try {
      const path = await resolveLiveTrainingRedirect(
        user,
        user?.permissions || []
      );
      if (path) {
        navigate(path);
        return;
      }
    } catch {
      /* fall through to stored session */
    }

    try {
      const session = JSON.parse(localStorage.getItem("rsessn") || "null");
      const stored = JSON.parse(localStorage.getItem("grpinf") || "null");
      if (session?.ts_id) {
        persistLiveTrainingSession(
          session,
          normalizeStoredGroupInfo(stored, session)
        );
      }
    } catch {
      /* ignore */
    }
    navigate("/live-training");
  };

  const showPlayerTrainingNav =
    !showCoachNav && !isAdminView && !isParent && showHomeNav;

  return (
    <>
      <div className="main-content-with-bottom-nav">
        {homeMounted && showHomeNav && (
          <div
            className="home-keep-alive"
            style={{ display: isHome ? undefined : "none" }}
            aria-hidden={!isHome}
          >
            <CoachHome isActive={isHome} />
          </div>
        )}
        {!isHome && <Outlet />}
      </div>
      <div className="bottom-nav">
        {showDashboardNav ? (
          <button
            type="button"
            className={
              splitLocation[1] === "Dashboard" ? "bottom-nav-link active" : "bottom-nav-link"
            }
            onClick={handleDashboardNav}
            data-tour-id="nav-dashboard"
            aria-label={t("hallNavOpenMenu", t("dashboard"))}
            title={t("hallNavOpenMenu", t("dashboard"))}
          >
            <FontAwesomeIcon icon={faGear} />
          </button>
        ) : null}
        <Link
          className={
            splitLocation[1] === "profile" ? "bottom-nav-link active" : "bottom-nav-link"
          }
          to={"/profile"}
          data-tour-id="nav-profile"
          aria-label={t("profile")}
        >
          <span
            className="bottom-nav-icon-mask"
            style={{
              WebkitMaskImage: `url(${user_svg})`,
              maskImage: `url(${user_svg})`,
            }}
            aria-hidden
          />
        </Link>
        {isParent ? (
          <Link
            className={pathname.startsWith("/parent/progress") ? "bottom-nav-link active" : "bottom-nav-link"}
            to={"/parent/progress"}
            data-tour-id="nav-parent-hub"
            aria-label={t("parentHub", "Progress Hub")}
          >
            <span
              className="bottom-nav-icon-mask"
              style={{
                WebkitMaskImage: `url(${home_svg})`,
                maskImage: `url(${home_svg})`,
              }}
              aria-hidden
            />
          </Link>
        ) : !showHomeNav ? null : (
          <Link
            className={splitLocation[1] === "home" ? "bottom-nav-link active" : "bottom-nav-link"}
            to={"/home"}
            data-tour-id="nav-home"
            aria-label={t("home")}
          >
            <span
              className="bottom-nav-icon-mask"
              style={{
                WebkitMaskImage: `url(${home_svg})`,
                maskImage: `url(${home_svg})`,
              }}
              aria-hidden
            />
          </Link>
        )}
        <Link
          className={
            splitLocation[1] === "competition" ? "bottom-nav-link active" : "bottom-nav-link"
          }
            to={"/competition"}
            data-tour-id="nav-competition"
            aria-label={t("competitionTitle", "Competition")}
        >
          <FontAwesomeIcon icon={faTrophy} />
        </Link>
        {showCoachNav ? (
          <>
            {coachNav.showPlanning ? (
              <Link
                className={
                  splitLocation[1] === "planning" ? "bottom-nav-link active" : "bottom-nav-link"
                }
                to={"/planning"}
                data-tour-id="nav-planning"
                aria-label={t("planning")}
              >
                <FontAwesomeIcon icon={faClipboardList} />
              </Link>
            ) : null}
            {coachNav.showTraining ? (
              <button
                type="button"
                className={
                  splitLocation[1] === "live-training"
                    ? "bottom-nav-link active bottom-nav-link--live"
                    : "bottom-nav-link"
                }
                onPointerDown={() => requestLiveFullscreen()}
                onClick={handleCoachLiveTrainingNav}
                data-tour-id="nav-training"
                aria-label={t("liveTrainingNavJoin", t("training_nav") || "Live training")}
                title={t("liveTrainingNavJoin", t("training_nav") || "Live training")}
              >
                {liveTrainingOpen ? (
                  <span className="bottom-nav-live-dot" aria-hidden />
                ) : null}
                <FontAwesomeIcon icon={faTableTennis} />
              </button>
            ) : null}
          </>
        ) : showPlayerTrainingNav ? (
          <button
            type="button"
            className={
              splitLocation[1] === "live-training"
                ? "bottom-nav-link active bottom-nav-link--live"
                : liveNav.visible
                  ? "bottom-nav-link bottom-nav-link--live"
                  : "bottom-nav-link"
            }
            onPointerDown={() => {
              if (liveNav.visible) requestLiveFullscreen();
            }}
            onClick={handleLiveTrainingNav}
            disabled={liveNav.joining}
            aria-label={
              liveNav.visible
                ? t("liveTrainingNavJoin", "Join live training")
                : t("playerNewTrainingSession", "Schedule a session")
            }
            data-tour-id="nav-live"
            title={
              liveNav.visible
                ? t("liveTrainingNavJoin", "Join live training")
                : t("playerNewTrainingSession", "Schedule a session")
            }
          >
            {liveNav.visible ? <span className="bottom-nav-live-dot" aria-hidden /> : null}
            <FontAwesomeIcon icon={faTableTennisPaddleBall} />
          </button>
        ) : null}
      </div>
    </>
  );
};
